import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { debounceTime, finalize, map, startWith, switchMap } from 'rxjs/operators';
import { TagsShowMoreComponent } from 'src/app/pages/buyer/company-search/modals/tags-show-more/tags-show-more.component';
import { CompanyPageRequestDto } from 'src/portal/search-api/models/company-page-request-dto';
import { FacetDto } from 'src/portal/search-api/models/facet-dto';
import { GlobalSearchResultTypeEnum } from 'src/portal/search-api/models/global-search-result-type-enum';
import { CompanyService } from 'src/portal/search-api/services/company.service';
import { SuggestService } from 'src/portal/search-api/services/suggest.service';
import { COUNTRY_FLAG_MAP } from '../../dictionares/country-flags';
import {
  formatTitleCaseWords,
  shouldFormatAsTitleCaseField,
} from '../../utils/text-formatting';

enum SearchType {
    Default,
    Product,
    Category,
    Vendor
}

@Component({
  selector: 'search-filter-autocomplete',
  templateUrl: './search-filter-autocomplete.component.html',
  styleUrls: ['./search-filter-autocomplete.component.scss'],
})
export class SearchFilterAutocompleteComponent implements OnInit {
  @Input() label: string;
  @Input() placeholder: string;
  @Input() items: string[] = [];
  @Input() searchField: string;
  @Input() iconName: string;
  @Input() expanded: boolean = false;
  @Input() searhType: any = GlobalSearchResultTypeEnum.COMPANY;

  @Output() itemsChange = new EventEmitter();

  @ViewChild('auto') auto: ElementRef;
  @ViewChild('input') input: ElementRef;

  addOnBlur = true;

  loading: boolean;
  inputControl = new FormControl();
  separatorKeysCodes: number[] = [ENTER, COMMA];
  filteredItems: Observable<any[]>;

  constructor(
    private suggestService: SuggestService,
    private companyService: CompanyService,
    public dialog: MatDialog,
    private cdr : ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.setupFilteredItems();
  }



 private setupFilteredItems() {
  this.filteredItems = this.inputControl.valueChanges.pipe(
    startWith(''),
    debounceTime(300),
    switchMap((value) => {
      return this._filter(value);
    }),
  );
}

remove(item: string): void {
  const index = this.items.indexOf(item);

  if (index >= 0) {
    this.items.splice(index, 1);
    this.itemsChange.emit(this.items);
    this.setupFilteredItems();
  }
}

  selected(event: MatAutocompleteSelectedEvent): void {
    if (!event.option) {
      return;
    }
    const value = event.option.value.name;

    if ((value || '').trim()) {
      this.items.push(value);

      this.itemsChange.emit(event.option);
      this.inputControl.setValue('');
    }

  }

  private  _filter(value: string): Observable<FacetDto[]> {
    this.loading = true;
    const filterValue = value.toLowerCase();
    if (this.searchField != 'companyName') {
      return this.suggestSearch(filterValue);
    } else {
      return this.companySearch(filterValue);
    }
  }

  suggestSearch(filterValue): Observable<FacetDto[]> {
    return this.suggestService
      .suggest({
        entity: this.searhType,
        field: this.searchField,
        search: filterValue,
        limit:50
      })
      .pipe(
        map((result: FacetDto[]) => {
          const filteredResult = result?.filter((item: FacetDto) => {
            return !this.items.some((otherItem: string) => {
              return item.name.toLowerCase() === otherItem.toLowerCase();
            });
          });
          return filteredResult;
        }),
        finalize(() => (this.loading = false))
      );
  }

  companySearch(filterValue): Observable<any[]> {
    let body: CompanyPageRequestDto = {
      pageNumber: 0,
      pageSize: 10,
      filters: {
        search: filterValue,
        filterType: GlobalSearchResultTypeEnum.COMPANY,
      },
      sort: [],
    };
    return this.companyService.findCompanies({ body: body }).pipe(
      map((res) => res.content?.map((x: any) => ({
        id: x.id,
        name: x.companyName,
        website: x.website
      }))),
      map((res: FacetDto[]) => res?.filter(item =>
        !this.items.some(selectedItem => selectedItem.toLowerCase() === item.name.toLowerCase())
      )),
      finalize(() => (this.loading = false))
    );
  }


  tagsShowMore() {
    const data = {
      selectedCompanyNames: this.searchField === "companyName" ? this.items : [],
      selectedIndustries: this.searchField === "industry" ? this.items : [],
      selectedTitles: this.searchField === "jobTitle" ? this.items : [],
      selectedSkills: this.searchField === "expertises" ? this.items : [],
      selectedWebsites: this.searchField === "website" ? this.items : [],
      selectedLocations: this.searchField === "location" ? this.items : [],
      selectedCountries: this.searchField === "country" ? this.items : [],
      showAllRemove: true
    };

    const dialogRef = this.dialog.open(TagsShowMoreComponent, {
      width: '500px',
      data: data,
    });

    dialogRef.afterClosed().subscribe((data) => {
      if (data) {
        console.log("Dialog data received:", data); // Проверяем, что данные приходят

        const updatedItems =
          this.searchField === "companyName" ? data.selectedCompanyNames :
          this.searchField === "industry" ? data.selectedIndustries :
          this.searchField === "jobTitle" ? data.selectedTitles :
          this.searchField === "expertises" ? data.selectedSkills :
          this.searchField === "website" ? data.selectedWebsites :
          this.searchField === "location" ? data.selectedLocations :
          this.searchField === "country" ? data.selectedCountries : [];

        this.items.length = 0; // Очищаем массив (не меняем ссылку!)
        this.items.push(...updatedItems); // Добавляем новые элементы

        this.itemsChange.emit(this.items);
        this.setupFilteredItems();
        this.cdr.detectChanges(); // Принудительно обновляем Angular
      }
    });
  }

  getFlagCode(name: string): string | null {
  const key = name.toLowerCase();
  return COUNTRY_FLAG_MAP[key] ?? null;
  }

  getDisplayName(value: string | null | undefined): string {
    if (shouldFormatAsTitleCaseField(this.searchField)) {
      return formatTitleCaseWords(value);
    }

    return value || '';
  }
}
